import {
  type PersistedActiveIntent,
  releasesHoldOnCancel as releasesHold,
} from './activeIntent';
import {
  isRefusedWith,
  RequestOutcomeUnknownError,
  UNKNOWN_READER_CODE,
} from './paymentStatus';

interface ReaderCancelObserver {
  recordEvent(
    category: string,
    message: string,
    data?: Record<string, unknown>
  ): void;
}

export interface ReaderCancelOptions {
  releaseHold: boolean;
}

export interface ReaderCancelRegistryDeps {
  cancelPaymentOnReader(
    input: PersistedActiveIntent,
    options: ReaderCancelOptions
  ): Promise<void>;
  // The defaults are Date.now and a timer.
  now?(): number;
  wait?(ms: number): Promise<void>;
  observer?: ReaderCancelObserver;
}

export interface CancelIntentOptions {
  force?: boolean;
  // The default comes from the stage of the intent (releasesHoldOnCancel).
  // The kiosk sets it when it gives up a capture-stage hold that the hold
  // step did not accept.
  releaseHold?: boolean;
}

export interface ReaderCancelRegistry {
  cancelIntent(
    intent: PersistedActiveIntent,
    successMessage: string,
    failureMessage: string,
    options?: CancelIntentOptions
  ): Promise<boolean>;
  waitForPendingCancels(): Promise<void>;
  // A send of the intent to the reader starts a new reader action. A retry
  // sends the same intent again, and that action needs its own cancel.
  forgetIntent(intent: PersistedActiveIntent): void;
  // The number of cancels that started. A send compares it before and after
  // its request.
  startedCancels(): number;
  // True when a cancel of the kiosk can be on the reader: a cancel in
  // flight, a cancel that started after `since`, or a cancel that had no
  // answer a short time ago.
  mayBeCancelling(since: number): boolean;
  // Waits for the cancels in flight and for the end of the time in which
  // the backend can continue a cancel that had no answer.
  waitForCancelsToEnd(): Promise<void>;
}

interface PendingCancel {
  promise: Promise<boolean>;
  releasesHold: boolean;
}

const MAX_REMEMBERED_CANCELLED_INTENTS = 100;
// The backend can continue a cancel after the kiosk stopped to wait for its
// answer. The kiosk waits 15 s for that answer, and the backend bounds its
// reader calls to less than that time. Thus one more period of the same
// length covers the remaining part of such a cancel.
const UNANSWERED_CANCEL_MS = 15_000;

// The cancel body carries release_hold for the same intents (see
// buildCancelPaymentOnReaderBody): only the backend can release an amount
// that the reader held in the last second.

// An earlier cancel of the same intent makes a new cancel unnecessary only
// when it also released the hold, or when the new cancel releases nothing.
function covers(earlierReleasesHold: boolean, releaseHold: boolean): boolean {
  return earlierReleasesHold || !releaseHold;
}

export function createReaderCancelRegistry(
  deps: ReaderCancelRegistryDeps
): ReaderCancelRegistry {
  const cancelInFlight = new Map<string, PendingCancel>();
  const readerCancelInFlight = new Map<string, Promise<boolean>>();
  // The key of each intent that a cancel ended, and true when that cancel
  // released the hold. A Map keeps the insertion order for the eviction.
  const cancelledIntents = new Map<string, boolean>();
  const now = deps.now ?? Date.now;
  const wait =
    deps.wait ??
    ((ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms)));
  let startedCancelCount = 0;
  let unansweredCancelEndsAt = 0;

  async function cancelIntent(
    intent: PersistedActiveIntent,
    successMessage: string,
    failureMessage: string,
    options: CancelIntentOptions = {}
  ): Promise<boolean> {
    const key = intentKey(intent);
    const force = options.force === true;
    const releaseHold = options.releaseHold ?? releasesHold(intent);
    const cancelled = cancelledIntents.get(key);
    if (!force && cancelled !== undefined && covers(cancelled, releaseHold)) {
      return true;
    }

    const pending = cancelInFlight.get(key);
    if (pending && !force && covers(pending.releasesHold, releaseHold)) {
      return pending.promise;
    }
    const entry: { promise?: Promise<boolean> } = {};
    // A cancel that releases the hold goes after the cancel in flight, thus
    // the reader gets one cancel at a time. A forced cancel does not wait.
    const previous = force ? undefined : pending?.promise;

    startedCancelCount += 1;
    const cancelPromise = (async () => {
      if (previous) await previous;
      try {
        await deps.cancelPaymentOnReader(intent, { releaseHold });
        rememberCancelledIntent(key, releaseHold);
        deps.observer?.recordEvent('entr.payment', successMessage, {
          // Full payment/reader IDs are intentional telemetry join keys.
          payment_intent_id: intent.paymentIntentId,
          reader_id: intent.readerId,
          release_hold: releaseHold,
        });
        return true;
      } catch (error) {
        // A reader that Stripe does not know has no action to cancel. A
        // failure here keeps the intent, and then each payment stops at the
        // same cancel.
        if (isRefusedWith(error, UNKNOWN_READER_CODE)) {
          rememberCancelledIntent(key, false);
          deps.observer?.recordEvent(
            'entr.payment',
            `${successMessage}: the reader is not on the Stripe account`,
            {
              payment_intent_id: intent.paymentIntentId,
              reader_id: intent.readerId,
            }
          );
          return true;
        }
        if (error instanceof RequestOutcomeUnknownError) {
          unansweredCancelEndsAt = now() + UNANSWERED_CANCEL_MS;
        }
        deps.observer?.recordEvent('entr.payment', failureMessage, {
          // Full payment/reader IDs are intentional telemetry join keys.
          payment_intent_id: intent.paymentIntentId,
          reader_id: intent.readerId,
          release_hold: releaseHold,
          error: error instanceof Error ? error.message : String(error),
        });
        return false;
      } finally {
        if (!force && cancelInFlight.get(key)?.promise === entry.promise) {
          cancelInFlight.delete(key);
        }
      }
    })();
    entry.promise = cancelPromise;

    const readerCancelKey = `${key}:${force ? 'force' : 'normal'}:${Date.now()}:${Math.random()}`;
    readerCancelInFlight.set(
      readerCancelKey,
      cancelPromise.finally(() => {
        readerCancelInFlight.delete(readerCancelKey);
      })
    );
    if (!force) {
      cancelInFlight.set(key, {
        promise: cancelPromise,
        releasesHold: releaseHold,
      });
    }
    return cancelPromise;
  }

  async function waitForPendingCancels(): Promise<void> {
    while (readerCancelInFlight.size > 0) {
      await Promise.all(readerCancelInFlight.values());
    }
  }

  function rememberCancelledIntent(key: string, releaseHold: boolean): void {
    const earlier = cancelledIntents.get(key);
    cancelledIntents.delete(key);
    cancelledIntents.set(key, releaseHold || earlier === true);

    while (cancelledIntents.size > MAX_REMEMBERED_CANCELLED_INTENTS) {
      const oldest = cancelledIntents.keys().next().value;
      if (oldest === undefined) break;
      cancelledIntents.delete(oldest);
    }
  }

  function forgetIntent(intent: PersistedActiveIntent): void {
    cancelledIntents.delete(intentKey(intent));
  }

  function mayBeCancelling(since: number): boolean {
    return (
      readerCancelInFlight.size > 0 ||
      startedCancelCount !== since ||
      now() < unansweredCancelEndsAt
    );
  }

  async function waitForCancelsToEnd(): Promise<void> {
    await waitForPendingCancels();
    const remainingMs = unansweredCancelEndsAt - now();
    if (remainingMs > 0) await wait(remainingMs);
  }

  return {
    cancelIntent,
    waitForPendingCancels,
    forgetIntent,
    startedCancels: () => startedCancelCount,
    mayBeCancelling,
    waitForCancelsToEnd,
  };
}

function intentKey(intent: PersistedActiveIntent): string {
  return [
    intent.paymentIntentId,
    intent.readerId,
    intent.sessionId,
    intent.idempotencyKey,
  ].join(':');
}
